import React, { useState, useEffect, useRef } from 'react';
import { Car, Truck, Bike, Caravan, Search, Loader2, CheckCircle2, X, BadgeCheck, AlertTriangle, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useForm } from '../../context/FormContext';
import { useSource } from '../../context/SourceContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { isValidUKVehicleReg, formatUKVehicleReg } from '../../lib/ukVehicleReg';
import { getMotMileageWarning } from '../../lib/motMileage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { id: 'Car', label: 'Car', icon: Car },
  { id: 'Van', label: 'Van', icon: Truck },
  { id: 'Motorbike', label: 'Motorbike', icon: Bike },
  { id: 'Caravan Motorhome', label: 'Caravan/Motorhome', icon: Caravan },
];

export const VehicleStep = () => {
  const { formData, updateFormData, applicationMode, setApplicationMode } = useForm();
  const { source } = useSource();
  const canSkipReg = source?.submission_mode === 'pdf_email';
  const noReg = canSkipReg && !!formData.vehicle_reg_unknown;
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupDone, setLookupDone] = useState(false);
  // Auto-lookup: debounced trigger so we run the DVLA call as soon as the user
  // has typed a plausibly-valid UK plate. Customers were typing the reg and
  // skipping the explicit "Lookup" button, which left vehicle_make empty and
  // got submissions rejected at the broker end.
  const autoLookupTimer = useRef(null);
  const lastAutoLookedReg = useRef('');
  // OCR sometimes populates vehicle_make with a value that doesn't match the
  // actual reg (e.g. Make=VOLKSWAGEN with Model=RANGER 2.0 EcoBlue — Ranger
  // is a Ford). To catch this we do a *silent* DVLA verification on blur and
  // compare against the OCR-populated make. Mismatch → surface an amber chip
  // asking the customer to hit the Lookup button (which overwrites with the
  // authoritative DVLA data).
  const verifiedRegRef = useRef({});
  const [makeMismatch, setMakeMismatch] = useState(null); // { ocrMake, dvlaMake, reg }

  // Credit-line / pre-approval flow available on all brokers — the customer's
  // borrow amount populates the cash-price slot in the broker payload, so we no
  // longer need broker-specific handling. Artis additionally swaps the finance
  // product to CreditLine (4) and sends placeholder vehicle data so its dummy
  // vehicle logic fires; AutoConvert just receives the borrow amount as the
  // cash price with empty vehicle fields.
  const supportsCreditLine = true;
  const isCreditLine = applicationMode === 'credit-line';

  const handleVehicleLookup = async () => {
    const reg = formData.vehicle_registration?.trim();
    if (!reg) {
      setLookupError('Enter a registration number first');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    setLookupDone(false);

    try {
      const response = await fetch(`${API_URL}/api/vehicle-lookup/${encodeURIComponent(reg)}`);
      const data = await response.json();

      if (data.success) {
        const updates = {
          vehicle_colour: data.colour || formData.vehicle_colour || '',
          vehicle_year: data.year_of_manufacture?.toString() || formData.vehicle_year || '',
          vehicle_fuel_type: data.fuel_type || formData.vehicle_fuel_type || '',
          vehicle_engine_capacity: data.engine_capacity?.toString() || formData.vehicle_engine_capacity || '',
          vehicle_date_first_registered: data.date_first_registered || formData.vehicle_date_first_registered || '',
          vehicle_co2_emissions: data.co2_emissions?.toString() || formData.vehicle_co2_emissions || '',
          vehicle_tax_status: data.tax_status || '',
          vehicle_mot_status: data.mot_status || '',
          vehicle_mot_expiry: data.mot_expiry_date || '',
        };
        if (data.make) updates.vehicle_make = data.make;
        if (data.model) updates.vehicle_model = data.model;
        if (data.vehicle_type) updates.vehicle_type = data.vehicle_type;
        updateFormData(updates);
        setLookupDone(true);
        // DVLA data has just overwritten the make — any previous mismatch
        // warning is now stale.
        setMakeMismatch(null);
      } else {
        setLookupError(data.message || 'Vehicle not found');
      }
    } catch (err) {
      setLookupError('Lookup failed. Please try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  const hasVehicleDetails = formData.vehicle_make || formData.vehicle_colour;
  const motWarning = getMotMileageWarning(formData);
  const motCheckedReg = useRef('');
  const latestModelRef = useRef('');
  latestModelRef.current = formData.vehicle_model;

  // Fetch DVSA MOT history once per valid reg so we can sanity-check the mileage.
  useEffect(() => {
    if (isCreditLine) return;
    const reg = (formData.vehicle_registration || '').replace(/\s+/g, '').toUpperCase();
    if (!isValidUKVehicleReg(reg) || reg === motCheckedReg.current) return;
    const timer = setTimeout(async () => {
      motCheckedReg.current = reg;
      updateFormData({ mot_last_mileage: '', mot_last_date: '' });
      try {
        const res = await fetch(`${API_URL}/api/mot-history/${encodeURIComponent(reg)}`);
        const data = await res.json();
        if (data.available && data.model && !latestModelRef.current) {
          updateFormData({ vehicle_model: data.model });
        }
        if (data.available && data.has_history) {
          updateFormData({
            mot_last_mileage: data.last_mileage ? String(data.last_mileage) : '',
            mot_last_date: data.last_date || '',
          });
        }
      } catch (_) {
        // Soft check only
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.vehicle_registration, isCreditLine]);

  // Auto-trigger DVLA lookup once the registration looks structurally valid.
  // UK plates are 7 characters in canonical form (no spaces — we strip them on
  // input). Debounce ~600ms after the last keystroke. Skip if we already looked
  // up this exact reg, if vehicle_make is already populated, or if we're in
  // credit-line mode (no vehicle selected).
  useEffect(() => {
    if (isCreditLine) return;
    const reg = (formData.vehicle_registration || '').replace(/\s+/g, '').toUpperCase();
    if (reg.length < 6 || reg.length > 8) return; // typical 7; allow 6-8 for personalised plates
    if (!isValidUKVehicleReg(reg)) return; // OCR junk like "2FBJXDC" — skip the DVLA call
    if (reg === lastAutoLookedReg.current) return;
    if (formData.vehicle_make) return; // already enriched manually or via OCR
    if (lookupLoading) return;
    clearTimeout(autoLookupTimer.current);
    autoLookupTimer.current = setTimeout(() => {
      lastAutoLookedReg.current = reg;
      handleVehicleLookup();
    }, 600);
    return () => clearTimeout(autoLookupTimer.current);
  }, [formData.vehicle_registration, isCreditLine, formData.vehicle_make]);

  // Silent DVLA verification on registration blur — only when vehicle_make is
  // already populated (typically by OCR). Purpose: catch OCR cross-contamination
  // like Make=VOLKSWAGEN + Model=RANGER by comparing against DVLA's canonical
  // make. Non-destructive: never overwrites the OCR data, just surfaces an
  // amber warning that prompts the customer to click Lookup.
  const verifyRegAgainstDVLA = async (reg) => {
    if (!reg || !isValidUKVehicleReg(reg)) return;
    if (verifiedRegRef.current[reg]) return;
    verifiedRegRef.current[reg] = true;
    try {
      const response = await fetch(`${API_URL}/api/vehicle-lookup/${encodeURIComponent(reg)}`);
      const data = await response.json();
      if (!data.success || !data.make) return;
      const dvlaMake = data.make.trim().toUpperCase();
      const ocrMake = (formData.vehicle_make || '').trim().toUpperCase();
      if (dvlaMake && ocrMake && dvlaMake !== ocrMake) {
        setMakeMismatch({ ocrMake: formData.vehicle_make, dvlaMake: data.make, reg });
      }
    } catch (_) {
      // Silent — the visible Lookup button and existing error UI already
      // surface hard failures. This is a soft verification only.
    }
  };

  const handleRegBlur = () => {
    // Only verify when we have an OCR-populated make to compare against.
    // Without OCR the debounced auto-lookup above already covers the
    // "customer forgot to click Lookup" case.
    if (!formData.vehicle_make) return;
    if (isCreditLine) return;
    const reg = (formData.vehicle_registration || '').replace(/\s+/g, '').toUpperCase();
    verifyRegAgainstDVLA(reg);
  };

  const clearVehicleDetails = () => {
    updateFormData({
      vehicle_make: '',
      vehicle_model: '',
      vehicle_derivative: '',
      vehicle_colour: '',
      vehicle_year: '',
      vehicle_fuel_type: '',
      vehicle_engine_capacity: '',
      vehicle_date_first_registered: '',
      vehicle_co2_emissions: '',
      vehicle_tax_status: '',
      vehicle_mot_status: '',
      vehicle_mot_expiry: '',
    });
    setLookupDone(false);
    setLookupError('');
  };

  const DetailRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
        <span className="text-slate-500 text-sm">{label}</span>
        <span className="font-medium text-slate-900 text-sm text-right">{value}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="vehicle-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {isCreditLine ? 'Finance Check Only' : 'Vehicle Details'}
        </h2>
        <p className="text-slate-600 hidden md:block">
          {isCreditLine
            ? 'Skip vehicle details — we\'ll just check what finance the applicant qualifies for'
            : applicationMode === 'full'
            ? (noReg ? 'Describe the vehicle and select the type' : 'Enter the vehicle registration and select the type')
            : 'Tell us what kind of application this is to continue'}
        </p>
      </div>

      {supportsCreditLine && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="application-mode-selector">
          <p className="text-sm font-medium text-slate-700 mb-3 text-center">What are you applying for?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setApplicationMode('full')}
              className={`text-left rounded-md border p-3 transition-colors ${applicationMode === 'full' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              data-testid="application-mode-full-btn"
            >
              <div className="flex items-center gap-2 font-medium text-sm text-slate-900">
                <Car className="w-4 h-4" /> A specific vehicle
              </div>
              <p className="text-xs text-slate-500 mt-1">I know which vehicle I want to finance</p>
            </button>
            <button
              type="button"
              onClick={() => setApplicationMode('credit-line')}
              className={`text-left rounded-md border p-3 transition-colors ${isCreditLine ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              data-testid="application-mode-credit-line-btn"
            >
              <div className="flex items-center gap-2 font-medium text-sm text-slate-900">
                <BadgeCheck className="w-4 h-4" /> Approval Check
              </div>
              <p className="text-xs text-slate-500 mt-1">No vehicle chosen yet / Credit line approval</p>
            </button>
          </div>
        </div>
      )}

      {/* Render the rest of the step only once a mode has been chosen — the choice
          stands alone until the user picks one, making it harder to miss. */}
      {applicationMode === null ? (
        <p className="text-center text-xs text-slate-400" data-testid="application-mode-prompt">
          Select an option above to continue
        </p>
      ) : (
      <>

      {isCreditLine && (
        <div
          className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 space-y-2"
          data-testid="credit-line-notice"
        >
          <p className="font-medium">No Vehicle selected for this application.</p>
          <p className="text-xs">
            We check what finance you&apos;ll qualify for first, you can update us later with a specific vehicle.
          </p>
        </div>
      )}

      {isCreditLine && (
        <div data-testid="borrow-amount-block">
          <Label htmlFor="borrow_amount" className="text-sm font-medium text-slate-700 mb-2 block">
            How much would you like to borrow? <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-medium">£</span>
            <Input
              id="borrow_amount"
              type="text"
              inputMode="numeric"
              placeholder="e.g. 15000"
              value={formData.borrow_amount || ''}
              onChange={(e) => {
                // Strip £, commas, spaces — keep digits + optional decimal.
                const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                updateFormData({ borrow_amount: cleaned });
              }}
              className="h-12 text-lg pl-8"
              data-testid="borrow-amount-input"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            The lender will use this as an indicative finance amount when running their approval check.
          </p>
        </div>
      )}

      {/* Vehicle Registration with Lookup */}
      {!isCreditLine && (
      <>
      {noReg && (
        <div data-testid="vehicle-description-block">
          <Label htmlFor="vehicle_description" className="text-sm font-medium text-slate-700 mb-2 block">
            Describe the vehicle <span className="text-red-500">*</span>
          </Label>
          <Input
            id="vehicle_description"
            placeholder="e.g. Blue Ford Fiesta 2018"
            value={formData.vehicle_description || ''}
            onChange={(e) => updateFormData({ vehicle_description: e.target.value })}
            className="h-12"
            data-testid="vehicle-description-input"
          />
          <p className="text-xs text-slate-500 mt-1">Colour, make, model and year help the dealer match the car to your application.</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => updateFormData({ vehicle_reg_unknown: false })}
            className="w-full h-11 mt-3 brand-border brand-text font-medium"
            data-testid="vehicle-have-reg-btn"
          >
            I have the registration
          </Button>
        </div>
      )}
      <div className={noReg ? 'hidden' : ''}>
        <Label htmlFor="vehicle_registration" className="text-sm font-medium text-slate-700 mb-2 block">
          Vehicle Registration <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          <Input
            id="vehicle_registration"
            placeholder="e.g. AB12 CDE"
            value={formatUKVehicleReg(formData.vehicle_registration)}
            onChange={(e) => {
              // Strip all whitespace — UK plates have no spaces in the canonical
              // form lenders expect (VN66ZWX, not "VN66 ZWX"). A space here causes
              // downstream payload errors with both AutoConvert and Artis.
              // Storage is canonical/unspaced; display gets the conventional
              // space re-inserted via formatUKVehicleReg above so the input
              // reads "AB12 CDE" while formData still holds "AB12CDE".
              const cleaned = e.target.value.replace(/\s+/g, '').toUpperCase();
              updateFormData({ vehicle_registration: cleaned });
              setLookupDone(false);
              // Clear any stale make-mismatch warning as soon as the reg is
              // edited — user's fixing it now, no need to nag on stale value.
              setMakeMismatch(null);
            }}
            onBlur={handleRegBlur}
            className="h-12 text-lg font-mono uppercase flex-1 tracking-wider"
            data-testid="vehicle-registration-input"
          />
          <button
            onClick={handleVehicleLookup}
            disabled={lookupLoading || !formData.vehicle_registration?.trim()}
            className="h-12 px-5 brand-bg hover:bg-[#008f42] disabled:bg-slate-300 text-white rounded-lg font-medium flex items-center gap-2 transition-colors whitespace-nowrap"
            data-testid="vehicle-lookup-btn"
          >
            {lookupLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Looking up...</>
            ) : lookupDone ? (
              <><CheckCircle2 className="w-4 h-4" /> Found</>
            ) : (
              <><Search className="w-4 h-4" /> Look Up</>
            )}
          </button>
        </div>
        {lookupError && (
          <div className="mt-2">
            <p className="text-red-500 text-sm">{lookupError}</p>
            <p className="text-slate-500 text-xs mt-1">You can still proceed with just the registration — vehicle details can be resolved by the lender.</p>
          </div>
        )}
        {/* Inline reg-format warning. OCR sometimes pulls a non-UK string into
            this field (e.g. a VIN fragment); without this hint the user can
            sail through Vehicle and only see the error in the Review panel. */}
        {formData.vehicle_registration?.trim() && !isValidUKVehicleReg(formData.vehicle_registration) && (
          <div
            className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md"
            data-testid="vehicle-reg-format-warning"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">
              <strong>"{formData.vehicle_registration}"</strong> doesn't look like a UK plate. Expected format e.g. AB12 CDE. Please double-check the registration.
            </p>
          </div>
        )}
        {/* OCR-vs-DVLA make mismatch warning. Fires when the OCR-populated
            vehicle_make disagrees with DVLA's canonical make for this reg
            (e.g. Make=VOLKSWAGEN with a plate whose real make is FORD).
            Non-destructive — customer must click Lookup to accept DVLA. */}
        {makeMismatch && (
          <div
            className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md"
            data-testid="reg-make-mismatch-warning"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">
              You entered <strong>{makeMismatch.ocrMake}</strong> as the make, but DVLA shows <strong>{makeMismatch.dvlaMake}</strong> for <strong>{makeMismatch.reg}</strong>. Click <strong>Lookup</strong> above to use the DVLA data, or update the make manually below.
            </p>
          </div>
        )}
        {canSkipReg && (
          <Button
            type="button"
            variant="outline"
            onClick={() => { clearVehicleDetails(); updateFormData({ vehicle_reg_unknown: true, vehicle_registration: '' }); }}
            className="w-full min-h-12 h-auto py-2 mt-3 border-2 brand-border brand-text font-semibold hover:opacity-80 whitespace-normal text-center"
            data-testid="vehicle-no-reg-btn"
          >
            <HelpCircle className="w-5 h-5 mr-2 shrink-0" />
            Don't know the reg? Describe the car instead
          </Button>
        )}
      </div>

      {/* Vehicle Summary Card */}
      {hasVehicleDetails && !noReg && (
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="vehicle-dvla-details">
          {/* Header */}
          <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {formData.vehicle_type === 'Van' ? <Truck className="w-5 h-5" /> : <Car className="w-5 h-5" />}
              <span className="font-semibold">
                {formData.vehicle_make}{formData.vehicle_model ? ` ${formData.vehicle_model}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-yellow-400 text-slate-900 font-bold px-3 py-0.5 rounded text-sm font-mono">
                {formData.vehicle_registration}
              </span>
              <button
                onClick={clearVehicleDetails}
                className="p-1.5 rounded-full hover:bg-slate-700 transition-colors"
                title="Clear vehicle details"
                data-testid="clear-vehicle-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="px-4 py-2">
              <DetailRow label="Make" value={formData.vehicle_make} />
              {/* Model is editable — DVLA does NOT return it. Optional: Artis enriches via CAP lookup if left blank. */}
              <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-500">Model</span>
                <input
                  type="text"
                  value={formData.vehicle_model || ''}
                  onChange={(e) => updateFormData({ vehicle_model: e.target.value.toUpperCase() })}
                  placeholder="Optional — leave blank if unsure"
                  className="text-sm text-right bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-500 focus:outline-none px-1 py-0.5 w-44 text-slate-900 font-medium placeholder:text-slate-300 placeholder:font-normal placeholder:italic"
                  data-testid="vehicle-model-input"
                />
              </div>
              {/* Derivative is also editable (trim level / variant) */}
              <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-500">Derivative</span>
                <input
                  type="text"
                  value={formData.vehicle_derivative || ''}
                  onChange={(e) => updateFormData({ vehicle_derivative: e.target.value.toUpperCase() })}
                  placeholder="e.g. SE NAV"
                  className="text-sm text-right bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-500 focus:outline-none px-1 py-0.5 w-44 text-slate-900 font-medium"
                  data-testid="vehicle-derivative-input"
                />
              </div>
              <DetailRow label="Colour" value={formData.vehicle_colour} />
              <DetailRow label="Year" value={formData.vehicle_year} />
              <DetailRow label="Fuel" value={formData.vehicle_fuel_type} />
            </div>
            <div className="px-4 py-2">
              <DetailRow label="Engine" value={formData.vehicle_engine_capacity ? `${formData.vehicle_engine_capacity}cc` : ''} />
              <DetailRow label="First Registered" value={formData.vehicle_date_first_registered} />
              <DetailRow label="Tax Status" value={formData.vehicle_tax_status} />
              <DetailRow label="MOT Status" value={formData.vehicle_mot_status} />
              <DetailRow label="MOT Expiry" value={formData.vehicle_mot_expiry} />
              <DetailRow label="CO2" value={formData.vehicle_co2_emissions ? `${formData.vehicle_co2_emissions} g/km` : ''} />
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Type Selection */}
      <div>
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          What type of vehicle? <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {VEHICLE_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => updateFormData({ vehicle_type: type.id })}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  formData.vehicle_type === type.id
                    ? 'brand-border brand-bg-light brand-text'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                }`}
                data-testid={`vehicle-type-${type.id.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-8 h-8" />
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mileage */}
      <div>
        <Label htmlFor="vehicle_mileage" className="text-sm font-medium text-slate-700 mb-2 block">
          Current Mileage {noReg ? <span className="text-slate-400 font-normal">(optional)</span> : <span className="text-red-500">*</span>}
        </Label>
        <Input
          id="vehicle_mileage"
          placeholder="e.g. 45000"
          value={formData.vehicle_mileage}
          onChange={(e) => updateFormData({ vehicle_mileage: e.target.value.replace(/\D/g, '') })}
          className="h-12"
          data-testid="vehicle-mileage-input"
        />
        {formData.mot_last_mileage && (
          <p className="text-xs text-slate-500 mt-1.5" data-testid="mot-last-mileage">
            Last MOT mileage: <span className="font-semibold">{Number(formData.mot_last_mileage).toLocaleString('en-GB')} mi</span> ({formData.mot_last_date})
          </p>
        )}
        {motWarning && (
          <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="mot-mileage-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">{motWarning}. Please double-check the odometer reading.</p>
          </div>
        )}
      </div>

      {/* VAT Qualifying Checkbox */}
      <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
        <input
          type="checkbox"
          id="vat_qualifying"
          checked={formData.vat_qualifying || false}
          onChange={(e) => updateFormData({ vat_qualifying: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 brand-text brand-focus"
          data-testid="vat-qualifying-checkbox"
        />
        <Label htmlFor="vat_qualifying" className="text-sm text-slate-700 cursor-pointer">
          VAT Qualifying Vehicle
        </Label>
        <span className="text-xs text-slate-400">Leave blank if unknown</span>
      </div>

      {/* Financials */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="cash_price" className="text-sm font-medium text-slate-700 mb-2 block">
            {formData.vat_qualifying ? (
              <>Net Price (ex-VAT) ({'\u00A3'}) <span className="text-red-500">*</span></>
            ) : (
              <>Cash Price inc. VAT ({'\u00A3'}) <span className="text-red-500">*</span></>
            )}
          </Label>
          <Input
            id="cash_price"
            placeholder={formData.vat_qualifying ? 'e.g. 10000 (NET, VAT added on top)' : 'e.g. 15000'}
            value={formData.cash_price}
            onChange={(e) => updateFormData({ cash_price: e.target.value.replace(/[^\d.]/g, '') })}
            className="h-12"
            data-testid="cash-price-input"
          />
          {formData.vat_qualifying ? (
            <p className="text-xs text-slate-500 mt-1" data-testid="cash-price-gross-preview">
              Enter the <strong>NET</strong> price the vehicle is advertised at (e.g. &ldquo;£10,000 + VAT&rdquo;).
              Gross (inc-VAT @ 20%): <strong className="brand-text">{'\u00A3'}{((parseFloat(formData.cash_price) || 0) * 1.2).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Enter the total price including any VAT</p>
          )}
          {(() => {
            // Sanity band — soft amber nudge for prices that are almost certainly a
            // finger-slip (e.g. dropped a zero, or added one). Doesn't block submission.
            const cp = parseFloat(formData.cash_price);
            if (!Number.isFinite(cp) || cp <= 0) return null;
            if (cp < 500) {
              return (
                <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="cash-price-low-warning">
                  Cash price looks low (£{cp.toLocaleString('en-GB')}) — please double-check.
                </p>
              );
            }
            if (cp > 200000) {
              return (
                <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="cash-price-high-warning">
                  Cash price looks high (£{cp.toLocaleString('en-GB')}) — please double-check.
                </p>
              );
            }
            return null;
          })()}
        </div>
        <div>
          <Label htmlFor="deposit" className="text-sm font-medium text-slate-700 mb-2 block">
            Deposit ({'\u00A3'})
          </Label>
          <Input
            id="deposit"
            placeholder="e.g. 1000"
            value={formData.deposit}
            onChange={(e) => updateFormData({ deposit: e.target.value.replace(/[^\d.]/g, '') })}
            className="h-12"
            data-testid="deposit-input"
          />
        </div>
      </div>

      {/* Part Exchange & Settlement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="part_exchange" className="text-sm font-medium text-slate-700 mb-2 block">
            Part Exchange Value ({'\u00A3'})
          </Label>
          <Input
            id="part_exchange"
            placeholder="e.g. 3000"
            value={formData.part_exchange}
            onChange={(e) => updateFormData({ part_exchange: e.target.value.replace(/[^\d.]/g, '') })}
            className="h-12"
            data-testid="part-exchange-input"
          />
        </div>
        <div>
          <Label htmlFor="settlement" className="text-sm font-medium text-slate-700 mb-2 block">
            Outstanding Finance Settlement ({'\u00A3'})
          </Label>
          <Input
            id="settlement"
            placeholder="e.g. 2000"
            value={formData.settlement}
            onChange={(e) => updateFormData({ settlement: e.target.value.replace(/[^\d.]/g, '') })}
            className="h-12"
            data-testid="settlement-input"
          />
        </div>
      </div>

      {/* Balance to Finance */}
      <div className="brand-bg-light border brand-border-light rounded-xl p-4">
        <div className="flex justify-between items-center">
          <div>
            <Label className="text-sm font-medium text-slate-700 block">Balance to Finance</Label>
            <p className="text-xs text-slate-500 mt-1">
              {formData.vat_qualifying
                ? 'Gross Price (Net + VAT) - Deposit - Part Exchange + Settlement'
                : 'Cash Price - Deposit - Part Exchange + Settlement'}
            </p>
          </div>
          <div className="text-2xl font-bold brand-text" data-testid="balance-to-finance">
            {'\u00A3'}{(() => {
              const rawPrice = parseFloat(formData.cash_price) || 0;
              const grossPrice = formData.vat_qualifying ? rawPrice * 1.2 : rawPrice;
              const deposit = parseFloat(formData.deposit) || 0;
              const partExchange = parseFloat(formData.part_exchange) || 0;
              const settlement = parseFloat(formData.settlement) || 0;
              return (grossPrice - deposit - partExchange + settlement).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            })()}
          </div>
        </div>
      </div>

      {/* VAT Amount */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex justify-between items-center">
          <div>
            <Label className="text-sm font-medium text-slate-700 block">VAT Amount</Label>
            <p className="text-xs text-slate-500 mt-1">
              {formData.vat_qualifying ? 'Net Price × 20% (added on top)' : 'VAT not applicable (Margin Scheme)'}
            </p>
          </div>
          <div className={`text-2xl font-bold ${formData.vat_qualifying ? 'text-amber-600' : 'text-slate-400'}`} data-testid="vat-amount">
            {'\u00A3'}{(() => {
              if (!formData.vat_qualifying) return '0.00';
              const netPrice = parseFloat(formData.cash_price) || 0;
              return (netPrice * 0.2).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            })()}
          </div>
        </div>
      </div>
      </>
      )}
      </>
      )}
    </div>
  );
};

export default VehicleStep;
